
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CreditCard, TrendingUp, Calendar, Gift, AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const GlowWalletV2 = () => {
  const [realTimeBalance, setRealTimeBalance] = useState(0);

  // Fetch user's current credit balance
  const { data: creditBalance = 0, refetch } = useQuery({
    queryKey: ['user-credit-balance'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return 0;
      
      const { data, error } = await supabase.rpc('get_user_credit_balance', {
        user_uuid: user.id
      });
      
      if (error) throw error;
      return data || 0;
    }
  });

  // Fetch credit transaction history
  const { data: transactions = [] } = useQuery({
    queryKey: ['credit-transactions'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      
      const { data, error } = await supabase
        .from('juv_credits')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    }
  });

  // Real-time credit balance updates
  useEffect(() => {
    setRealTimeBalance(creditBalance);
    
    const channel = supabase
      .channel('credit-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'juv_credits'
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [creditBalance, refetch]);

  // Calculate expiring credits
  const expiringCredits = transactions
    .filter(t => t.expires_at && new Date(t.expires_at) <= new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))
    .reduce((sum, t) => sum + (t.transaction_type === 'earned' ? t.amount : 0), 0);

  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'earned': return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'spent': return <CreditCard className="h-4 w-4 text-red-600" />;
      case 'bonus': return <Gift className="h-4 w-4 text-juv-terracotta" />;
      case 'referral': return <Gift className="h-4 w-4 text-juv-deep-chestnut" />;
      default: return <CreditCard className="h-4 w-4" />;
    }
  };

  const getTransactionColor = (type: string) => {
    switch (type) {
      case 'earned': return 'text-green-600';
      case 'spent': return 'text-red-600';
      case 'bonus': return 'text-juv-terracotta';
      case 'referral': return 'text-juv-deep-chestnut';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Header */}
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-juv-deep-chestnut">
          Glow Wallet
        </h1>
        <p className="text-lg text-juv-deep-chestnut/70">
          Track your credits and unlock your beauty potential
        </p>
      </div>

      {/* Balance Overview */}
      <Card className="border-juv-terracotta/30 bg-gradient-to-r from-juv-soft-blush to-white">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-juv-deep-chestnut">
            <CreditCard className="h-6 w-6" />
            <span>Current Balance</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center space-y-4">
            <div className="text-5xl font-bold text-juv-terracotta">
              {realTimeBalance}
            </div>
            <p className="text-lg text-juv-deep-chestnut/70">JUV Credits</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-deep-chestnut">
                  ${(realTimeBalance * 50).toFixed(0)}
                </div>
                <div className="text-sm text-juv-deep-chestnut/70">Treatment Value</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-deep-chestnut">
                  {transactions.filter(t => t.transaction_type === 'earned').length}
                </div>
                <div className="text-sm text-juv-deep-chestnut/70">Credits Earned</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-juv-deep-chestnut">
                  {transactions.filter(t => t.transaction_type === 'spent').length}
                </div>
                <div className="text-sm text-juv-deep-chestnut/70">Treatments</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Expiring Credits Alert */}
      {expiringCredits > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-800">
                  {expiringCredits} credits expiring soon
                </p>
                <p className="text-sm text-orange-700">
                  Book your treatments within 30 days to use these credits
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Transaction History */}
      <Card className="border-juv-rose-clay/20 bg-white">
        <CardHeader>
          <CardTitle className="text-juv-deep-chestnut">Transaction History</CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length > 0 ? (
            <div className="space-y-4">
              {transactions.map((transaction) => (
                <div key={transaction.id} className="flex items-center justify-between p-4 border border-juv-rose-clay/20 rounded-lg">
                  <div className="flex items-center space-x-3">
                    {getTransactionIcon(transaction.transaction_type)}
                    <div>
                      <p className="font-medium text-juv-deep-chestnut">
                        {transaction.description || `${transaction.transaction_type} credits`}
                      </p>
                      <p className="text-sm text-juv-deep-chestnut/70">
                        {new Date(transaction.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${getTransactionColor(transaction.transaction_type)}`}>
                      {transaction.transaction_type === 'spent' ? '-' : '+'}{transaction.amount}
                    </p>
                    {transaction.expires_at && (
                      <p className="text-xs text-gray-500">
                        Expires {new Date(transaction.expires_at).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CreditCard className="h-12 w-12 text-juv-terracotta mx-auto mb-4" />
              <p className="text-juv-deep-chestnut/70">No transactions yet</p>
              <Button
                asChild
                className="mt-4 bg-juv-terracotta hover:bg-juv-deep-chestnut text-white"
              >
                <a href="/v2/patients/refer">Earn Your First Credits</a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Earn More Credits */}
      <Card className="border-juv-terracotta/30 bg-gradient-to-r from-juv-soft-blush to-white">
        <CardHeader>
          <CardTitle className="text-juv-deep-chestnut">Earn More Credits</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button
              asChild
              variant="outline"
              className="h-16 text-juv-terracotta border-juv-terracotta hover:bg-juv-soft-blush"
            >
              <a href="/v2/patients/refer" className="flex items-center space-x-2">
                <Gift className="h-5 w-5" />
                <span>Refer Friends (+50 credits)</span>
              </a>
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-16 text-juv-terracotta border-juv-terracotta hover:bg-juv-soft-blush"
            >
              <a href="/v2/patients/membership" className="flex items-center space-x-2">
                <TrendingUp className="h-5 w-5" />
                <span>Upgrade to Member</span>
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GlowWalletV2;
